import { LinkingOptions } from '@react-navigation/native';
import { getPathFromState as getPathFromStateDefault } from '@react-navigation/core';
import type { NavigationState, PartialState } from '@react-navigation/routers';
import { RootStackParamList } from '../types/navigation';

/** Map tab/screen names to URL section so web and mobile use the same path (e.g. /dashboard/color-generator). */
const routeNameToSection: Record<string, string> = {
  HomeTab: 'home',
  MoodBoardTab: 'mood-boards',
  ColourAnalysis: 'color-analysis',
  VirtualDrapeTest: 'virtual-drape-test',
  ColorGenerator: 'color-generator',
  SmartWardrobe: 'smart-wardrobe',
  SettingsTab: 'settings',
  ProfileTab: 'profile',
  ColourAnalysisMobile: 'color-analysis',
  VirtualDrapeTestMobile: 'virtual-drape-test',
  ColorGeneratorMobile: 'color-generator',
  MoodBoardTabMobile: 'mood-boards',
  ProfileTabMobile: 'profile',
  SmartWardrobeMobile: 'smart-wardrobe',
  SettingsTabMobile: 'settings',
};

function getFocusedRouteName(
  state: NavigationState | PartialState<NavigationState>
): string | undefined {
  const route =
    typeof state.index === 'number'
      ? state.routes[state.index]
      : state.routes[state.routes.length - 1];
  if (route.state) {
    return getFocusedRouteName(route.state as NavigationState);
  }
  return route.name;
}

/** Find MainTabs route in state tree (state.routes[state.index] or any nested route named MainTabs). */
function findMainTabsRoute(
  state: NavigationState | PartialState<NavigationState> | undefined
): { params?: { section?: string }; state?: NavigationState } | undefined {
  if (!state?.routes?.length) return undefined;
  const route = state.routes[
    typeof state.index === 'number' ? state.index : 0
  ] as {
    name: string;
    params?: { section?: string };
    state?: NavigationState;
  };
  if (route?.name === 'MainTabs') return route;
  for (const r of state.routes) {
    const candidate = r as {
      name: string;
      params?: { section?: string };
      state?: NavigationState;
    };
    if (candidate.name === 'MainTabs') return candidate;
    if (candidate.state) {
      const found = findMainTabsRoute(candidate.state as NavigationState);
      if (found) return found;
    }
  }
  return undefined;
}

/**
 * Normalize section so we never output /dashboard/undefined (e.g. on mobile when params.section isn't set).
 */
function normalizeSection(section: string | undefined | null): string {
  if (section == null || section === '' || section === 'undefined')
    return 'home';
  return section;
}

/**
 * Custom getPathFromState so MainTabs always use path-only URLs (e.g. /dashboard/color-generator)
 * and never expose internal tab names like ColorGeneratorMobile in the URL.
 */
function getPathFromState(
  state: NavigationState | PartialState<NavigationState> | undefined,
  config: Parameters<typeof getPathFromStateDefault>[1]
): string {
  const mainTabs = findMainTabsRoute(state);
  if (mainTabs) {
    const fromParams = mainTabs.params?.section;
    const fromState = mainTabs.state
      ? routeNameToSection[getFocusedRouteName(mainTabs.state) ?? '']
      : undefined;
    const section = normalizeSection(fromParams ?? fromState ?? 'home');
    return section === 'home' ? '/dashboard' : `/dashboard/${section}`;
  }
  const path = getPathFromStateDefault(
    state as Parameters<typeof getPathFromStateDefault>[0],
    config
  );
  // Sanitize any /undefined segment in the path.
  // The regex /\/undefined(\?.*)?$/ only caught trailing occurrences; we now also
  // catch leading ones (e.g. /undefined/admin/dashboard produced when hash is
  // undefined in the admin route) and redirect safely to /dashboard.
  if (path && path.includes('/undefined')) {
    if (/^\/undefined(\/|$|\?)/.test(path)) {
      // Leading /undefined means a required param (e.g. :hash) was undefined.
      // Redirect to root rather than exposing a broken URL.
      return '/';
    }
    const sanitized = path.replace(/\/undefined(\?.*)?$/, (_, q) => q ?? '');
    return sanitized || '/';
  }
  return path;
}

import { getAuthLinkingPrefixes } from '../utils/authSignInSignUp';

/** Web app origins for deep linking (must include production/staging, not just localhost). */
const linkingPrefixes = getAuthLinkingPrefixes(process.env.EXPO_PUBLIC_APP_URL);

/**
 * Linking configuration for web URL routing
 * Same path for web and mobile (e.g. /dashboard/color-generator); layout (web vs mobile) is conditional.
 */
const linking: LinkingOptions<RootStackParamList> = {
  prefixes: linkingPrefixes,
  getPathFromState,
  config: {
    // Add debugging
    initialRouteName: 'Landing',
    screens: {
      // Landing page
      Landing: '',

      // Public feature screens (no sign-in - separate routes under /public)
      PublicColorGenerator: 'public/color-generator',
      PublicExploreColours: 'public/explore-colours',
      PublicColorAnalysis: 'public/color-analysis',
      PublicVirtualDrape: 'public/virtual-drape',

      // Auth screens
      Login: 'sign-in',
      ForgotPassword: 'forgot-password',
      AuthCallback: 'auth/callback',
      VerifyEmailRequired: 'verify-email',
      Register: {
        path: 'sign-up/:inviteCode?',
        parse: {
          inviteCode: (inviteCode: string) => inviteCode || undefined,
        },
      },
      Onboarding: 'onboarding',
      Invite: {
        path: 'invite/:code',
        parse: {
          code: (code: string) => code,
        },
      },
      AdminLogin: {
        path: ':hash/admin/login',
        parse: {
          hash: (hash: string) => hash || undefined,
        },
      },
      Admin: {
        path: ':hash/admin/:section?',
        parse: {
          hash: (hash: string) => hash || undefined,
          section: (section: string) => {
            const validSections = [
              'dashboard',
              'users',
              'subscriptions',
              'moodboards',
              'feedback',
              'flagged',
              'audit-logs',
              'analytics',
              'support',
              'permissions',
              'profile',
              'invites',
              'feature-flags',
            ];
            return validSections.includes(section)
              ? (section as
                  | 'dashboard'
                  | 'users'
                  | 'subscriptions'
                  | 'moodboards'
                  | 'feedback'
                  | 'flagged'
                  | 'audit-logs'
                  | 'analytics'
                  | 'support'
                  | 'permissions'
                  | 'profile')
              : 'dashboard';
          },
        },
        stringify: {
          section: (section: string) => section || 'dashboard',
        },
      },

      // Dashboard: path-only URLs (e.g. /dashboard/color-generator). No query params.
      MainTabs: {
        path: 'dashboard/:section?',
        parse: {
          section: (section: string) => {
            // Normalize section: handle undefined, empty string, or 'undefined' string
            // Return 'home' as default to ensure params are always set
            if (!section || section === 'undefined' || section === '')
              return 'home';
            return section;
          },
        },
        stringify: {
          section: (section: string) => {
            // Don't stringify 'home' section - it should be /dashboard, not /dashboard/home
            // Return undefined instead of empty string to preserve params
            if (!section || section === 'home' || section === 'undefined')
              return undefined;
            return section;
          },
        },
      },

      // Core features
      ColourDetail: 'dashboard/color-analysis/:profileId',
      WearThisToday: 'dashboard/outfit-suggestions',

      // Mood Board screens with dynamic routing
      MoodBoard: 'mood-boards', // Keep this for direct access (guest mode)
      MoodBoardDetail: {
        path: 'dashboard/mood-board/:boardId',
        parse: {
          boardId: (boardId: string) => boardId,
        },
        stringify: {
          boardId: (boardId: string) => boardId,
        },
      },

      // Blog screens (public access)
      BlogList: 'blog',
      BlogArticle: {
        path: 'blog/:articleId',
        parse: {
          articleId: (articleId: string) => articleId,
        },
        stringify: {
          articleId: (articleId: string) => articleId,
        },
      },

      // Legal and support pages (public access)
      FAQ: 'faq',
      PrivacyPolicy: 'privacy',
      TermsOfService: 'terms',

      // Product detail screens
      ColorAnalysisDetail: 'dashboard/features/color-analysis',
      VirtualDrapeDetail: 'dashboard/features/virtual-drape',
      SmartWardrobeDetail: 'dashboard/features/smart-wardrobe',
      PaletteGeneratorDetail: 'dashboard/features/palette-generator',
      StyleConsultationDetail: 'dashboard/features/style-consultation',
      MakeupMatchingDetail: 'dashboard/features/makeup-matching',

      // User screens
      UserProfile: 'dashboard/profile',
      AccessibilitySettings: 'dashboard/settings/accessibility',
      LanguageSelection: 'dashboard/settings/language',
      ThemeSelection: 'dashboard/settings/theme',

      // Subscription
      PremiumPaywall: 'dashboard/premium',

      // Shops
      Shops: 'dashboard/shops',
    },
  },
};

export default linking;
